// src/admin/screens/admins/AdminDetailScreen.tsx
// Super-admin management UI for a single admin_users row. Hero header
// (avatar + name + role chip + email/role meta) plus a Status card with
// 4 stat tiles and an Actions card with the four privileged operations:
//   - Change role (audit: admin.change_role)
//   - Toggle read-only override (audit: admin.set_readonly)
//   - Reset TOTP via admin_reset_totp RPC (audit: admin.reset_totp)
//   - Deactivate (sets is_active=false; audit: admin.deactivate)
//
// Self-protection: a super-admin cannot change their own role or
// deactivate themselves. The UI disables the buttons + shows a hint.
// RLS also blocks these writes server-side as defence-in-depth.
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Card from '@admin/components/Card';
import Button from '@admin/components/Button';
import StatusChip from '@admin/components/StatusChip';
import Skeleton from '@admin/components/Skeleton';
import Avatar from '@admin/components/Avatar';
import { useAdmin } from '@admin/hooks/useAdmin';
import { useRole } from '@admin/auth/useRole';
import { useAuth } from '@admin/auth/useAuth';
import { formatCatDateTime } from '@admin/utils/dates';
import ChangeRoleModal from './modals/ChangeRoleModal';
import ToggleReadonlyModal from './modals/ToggleReadonlyModal';
import ResetTotpModal from './modals/ResetTotpModal';
import DeactivateAdminModal from './modals/DeactivateAdminModal';
import styles from './AdminDetailScreen.module.css';

type OpenModal = null | 'role' | 'readonly' | 'reset_totp' | 'deactivate';

export default function AdminDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { can } = useRole();
  const { admin: me } = useAuth();
  const { data, isLoading, error, refetch } = useAdmin(id);
  const [modal, setModal] = useState<OpenModal>(null);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingStack}>
          <Skeleton h={28} />
          <Skeleton h={18} />
          <Skeleton h={18} />
          <Skeleton h={18} />
          <Skeleton h={18} />
          <Skeleton h={18} />
        </div>
      </div>
    );
  }
  if (error) {
    return <div className={styles.page}><p className={styles.errorLine}>{(error as Error).message}</p></div>;
  }
  if (!data) return <div className={styles.page}><p>Admin not found.</p></div>;

  const isSelf = me?.id === data.id;
  const closeWith = (ok: boolean) => {
    setModal(null);
    if (ok) void refetch();
  };

  return (
    <div className={styles.page}>
      <Link to="/admins" className={styles.back}>
        <ChevronLeft size={14} /> Admins
      </Link>

      <div className={styles.heroRow}>
        <div className={styles.heroLeft}>
          <Avatar name={data.display_name} size={56} />
          <div className={styles.heroText}>
            <div className={styles.heroChips}>
              <code className={styles.code}>{data.role}</code>
              {data.is_active
                ? <StatusChip label="Active" tone="positive" />
                : <StatusChip label="Deactivated" tone="danger" />}
              {data.is_readonly && <StatusChip label="Read-only" tone="warn" />}
            </div>
            <h1 className={styles.heroTitle}>{data.display_name}</h1>
            <p className={styles.heroSub}>{data.email}</p>
          </div>
        </div>
      </div>

      <Card title="Status">
        <div className={styles.statGrid}>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>Account</p>
            {data.is_active
              ? <StatusChip label="Active" tone="positive" />
              : <StatusChip label="Deactivated" tone="danger" />}
          </div>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>TOTP</p>
            {data.totp_enrolled_at
              ? <StatusChip label="Enrolled" tone="positive" />
              : <StatusChip label="Pending" tone="warn" />}
          </div>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>Mode</p>
            {data.is_readonly
              ? <StatusChip label="Read-only" tone="warn" />
              : <span className={styles.muted}>Read-write</span>}
          </div>
          <div className={styles.statTile}>
            <p className={styles.statLabel}>Last login</p>
            <p className={styles.statValue}>
              {data.last_login_at ? formatCatDateTime(data.last_login_at) : <span className={styles.muted}>Never</span>}
            </p>
          </div>
        </div>
      </Card>

      <Card title="Actions">
        <div className={styles.btnRow}>
          <Button
            onClick={() => setModal('role')}
            disabled={!can('admins.change_role') || isSelf}
          >
            Change role
          </Button>
          <Button
            variant="secondary"
            onClick={() => setModal('readonly')}
            disabled={!can('admins.set_readonly')}
          >
            {data.is_readonly ? 'Remove read-only' : 'Set read-only'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setModal('reset_totp')}
            disabled={!can('admins.reset_totp')}
          >
            Reset TOTP
          </Button>
          <Button
            variant="danger"
            onClick={() => setModal('deactivate')}
            disabled={!can('admins.deactivate') || isSelf || !data.is_active}
          >
            Deactivate
          </Button>
        </div>
        {isSelf && (
          <p className={styles.hint}>
            You cannot change your own role or deactivate yourself. Ask another super-admin.
          </p>
        )}
      </Card>

      {modal === 'role' && <ChangeRoleModal admin={data} onClose={closeWith} />}
      {modal === 'readonly' && <ToggleReadonlyModal admin={data} onClose={closeWith} />}
      {modal === 'reset_totp' && <ResetTotpModal admin={data} onClose={closeWith} />}
      {modal === 'deactivate' && <DeactivateAdminModal admin={data} onClose={closeWith} />}
    </div>
  );
}
